import { difference, every, flatten, intersection, mapValues, maxBy, noop, sortBy } from 'lodash-es'
import { DataWarehouseAvailabilityResponse, DataWarehouseProvider, DataWarehouseReportGrouping } from './hooks'
import { isAfter, isBefore, max, min } from 'date-fns'
import { DateType, PlatformUnit } from '@percept/types'
import { dateRangeCalculators, DateRangePresetOption, DateRangeValue } from '@percept/mui'
import { useUserPlatformUnits } from '@percept/hooks'
import { useMemo } from 'react'
import { isoDate, isPlatformUnitContainer } from '@percept/utils'
import { DateRangeUrlState } from '@percept/app-components'


type DateRangeMapping = {
  start: Date
  end: Date
}

type ProviderAvailability = DateRangeMapping & {
  provider: DataWarehouseProvider
}

export type ProviderAvailabilityByGrouping = Record<string, ProviderAvailability[]>

export type ProviderAvailabilityMapping = Record<DataWarehouseProvider, DateRangeMapping>

export type OrgProviderAvailability = Record<string, ProviderAvailabilityMapping>

export type OrgProviderAvailabilityByGrouping = Record<string, OrgProviderAvailability>


export const providerOrder: DataWarehouseProvider[] = [
  'GOOGLE_ADS',
  'FACEBOOK',
  'DV360',
  'ADFORM',
  'TIKTOK',
  'AMAZON_ADS',
]

export const getProviderAvailabilityByGrouping = (
  availabilityResponse: DataWarehouseAvailabilityResponse[] | undefined
): ProviderAvailabilityByGrouping => {
  if( !availabilityResponse ){
    return {}
  }
  const mapping = availabilityResponse.reduce( (acc, a) => {
    acc[a.grouping] = acc[a.grouping] || {}
    acc[a.grouping][a.provider] = acc[a.grouping][a.provider] || {}
    acc[a.grouping][a.provider] = {
      start: new Date(a.start),
      end: new Date(a.end),
    }
    return acc
  }, {} as Record<string, Record<DataWarehouseProvider, DateRangeMapping>>)

  return mapValues(
    mapping,
    (availabilityMapping) => {
      const availableProviders = Object.keys(availabilityMapping) as DataWarehouseProvider[]
      const orderedProviders = [
        ...intersection(providerOrder, availableProviders),
        ...difference(availableProviders, providerOrder).sort(),
      ]
      return orderedProviders.map( provider => ({
        provider,
        ...availabilityMapping[provider],
      }))
    }
  )
}

const getCollatedPlatformUnitIds = (
  orgUnit: PlatformUnit
): Record<string, string[]> => {
  const walk = (o: PlatformUnit): string[] => {
    let ids = [o.id]
    if( isPlatformUnitContainer(o) && o.children ){
      ids = [...ids, ...flatten(o.children.map(walk))]
    }
    return ids
  }

  return {
    [orgUnit.id]: walk(orgUnit),
    ...(orgUnit.children && orgUnit.children.reduce( (acc, childOrg) => {
      acc[childOrg.id] = walk(childOrg)
      return acc
    }, {} as Record<string, string[]>)),
  }
}


export const getOrgProviderAvailabilityByGrouping = (
  availabilityResponse: DataWarehouseAvailabilityResponse[] | undefined,
  orgUnit?: PlatformUnit | null,
): OrgProviderAvailabilityByGrouping => {
  if( !availabilityResponse ){
    return {}
  }

  if( !orgUnit ){
    return {}
  }

  const parentChildIdsMapping = getCollatedPlatformUnitIds(orgUnit)

  const childParentIdsMapping: Record<string, string[]> = (
    Object.entries(parentChildIdsMapping).reduce( (acc, [parentId, childIds]) => {
      childIds.forEach( childId => {
        acc[childId] = acc[childId] || []
        acc[childId].push(parentId)
      })
      return acc
    }, {} as Record<string, string[]>)
  )

  const collatedMapping = availabilityResponse.reduce( (acc, a) => {
    acc[a.grouping] = acc[a.grouping] || {}
    const parentIds = childParentIdsMapping[a.org_unit_id] || [a.org_unit_id]
    for( const parentId of parentIds ){
      acc[a.grouping][parentId] = acc[a.grouping][parentId] || {}
      acc[a.grouping][parentId][a.provider] = acc[a.grouping][parentId][a.provider] || {
        start: [],
        end: [],
      }
      acc[a.grouping][parentId][a.provider].start.push(new Date(a.start))
      acc[a.grouping][parentId][a.provider].end.push(
        a.has_active_accounts ?
          new Date(a.end) :
          new Date()
      )
    }
    return acc
  }, {} as Record<string, Record<string, Record<DataWarehouseProvider, Record<'start' | 'end', Date[]>>>>)

  const derivedMapping: OrgProviderAvailabilityByGrouping = mapValues(
    collatedMapping,
    byParentOrg => (
      mapValues(
        byParentOrg,
        byProvider => (
          mapValues(
            byProvider,
            availabilityDates => ({
              start: max(availabilityDates.start),
              end: min(availabilityDates.end),
            }) as DateRangeMapping
          )
        )
      )
    )
  )

  return derivedMapping
}

export const getAvailableReportGroupings = ({
  availability,
  reportGroupings,
}: {
  availability: DataWarehouseAvailabilityResponse[] | undefined | null
  reportGroupings: DataWarehouseReportGrouping[] | undefined | null
}): DataWarehouseReportGrouping[] => {
  if( !availability || !reportGroupings ){
    return []
  }
  const availableGroupings = availability.reduce( (acc, a) => {
    acc[a.grouping] = true
    return acc
  }, {} as Record<string, true>)
  return reportGroupings.filter( g => !!availableGroupings[g.key] )
}


export const getProviderAvailability = ({
  reportType,
  availabilityByGrouping,
  reportGrouping,
}: {
  reportType: string | undefined
  availabilityByGrouping: ProviderAvailabilityByGrouping | null
  reportGrouping: DataWarehouseReportGrouping | null
}): ProviderAvailability[] => {
  if( !reportType || !availabilityByGrouping || !reportGrouping ){
    return []
  }
  return (availabilityByGrouping[reportType] || []).filter( a => (
    reportGrouping.supported_providers.includes(a.provider)
  ))
}

export const getOrgProviderAvailability = ({
  orgAvailabilityByGrouping,
  reportGrouping,
}: {
  orgAvailabilityByGrouping: OrgProviderAvailabilityByGrouping | null
  reportGrouping: DataWarehouseReportGrouping | null
}): OrgProviderAvailability => {
  if( !orgAvailabilityByGrouping || !reportGrouping ){
    return {}
  }
  return mapValues(
    orgAvailabilityByGrouping[reportGrouping.key] || {},
    orgAvailability => (
      Object.entries(orgAvailability).reduce( (acc, [k, v]) => {
        if( reportGrouping.supported_providers.includes(k as DataWarehouseProvider) ){
          acc[k as DataWarehouseProvider] = v
        }
        return acc
      }, {} as ProviderAvailabilityMapping)
    )
  )
}

export const resolveProviderAvailability = ({
  orgUnitIds,
  orgProviderAvailability,
}: {
  orgUnitIds: string[]
  orgProviderAvailability: OrgProviderAvailability
}): ProviderAvailability[] => {
  const collatedAvailability = orgUnitIds.reduce( (acc, orgUnitId) => {
    const orgAvailability = orgProviderAvailability[orgUnitId]
    if( orgAvailability ){
      for( const [provider, availability] of Object.entries(orgAvailability) ){
        acc[provider as DataWarehouseProvider] = acc[provider as DataWarehouseProvider] || {
          startDates: [],
          endDates: [],
        }
        acc[provider as DataWarehouseProvider].startDates.push(availability.start)
        acc[provider as DataWarehouseProvider].endDates.push(availability.end)
      }
    }
    return acc
  }, {} as Record<DataWarehouseProvider, { startDates: Date[], endDates: Date[] }>)

  const validProviders = intersection(providerOrder, Object.keys(collatedAvailability) as DataWarehouseProvider[])
  return validProviders.map( provider => ({
    provider,
    start: max(collatedAvailability[provider].startDates),
    end: min(collatedAvailability[provider].endDates),
  }))
}


export const getSupportedProviders = ({
  rootOrgUnitId,
  reportGrouping,
  orgProviderAvailability,
}: {
  rootOrgUnitId: string | null
  reportGrouping: DataWarehouseReportGrouping | null
  orgProviderAvailability: OrgProviderAvailability
}): DataWarehouseProvider[] => {
  if( !rootOrgUnitId || !reportGrouping || !orgProviderAvailability[rootOrgUnitId] ){
    return []
  }
  return intersection(
    providerOrder,
    reportGrouping.supported_providers,
    Object.keys(orgProviderAvailability[rootOrgUnitId]) as DataWarehouseProvider[],
  )
}


export const getLatestProviderOverlap = (
  providerAvailability: ProviderAvailability[]
): DataWarehouseProvider[] => {
  const latestProvider = maxBy(providerAvailability, a => a.end.getTime())
  if( !latestProvider ){
    return []
  }

  const orderedEndOverlap = sortBy(
    providerAvailability.filter( a => (
      a.end.getTime() >= latestProvider.start.getTime()
    )),
    a => a.end.getTime()
  ).reverse()

  const matchingOverlap: ProviderAvailability[] = []
  for( const a of orderedEndOverlap ){
    if( !matchingOverlap.length ){
      matchingOverlap.push(a)
      continue
    }
    const isMatch = every(
      matchingOverlap,
      m => a.end.getTime() > m.start.getTime()
    )
    if( isMatch ){
      matchingOverlap.push(a)
    }
  }

  return matchingOverlap.map( a => a.provider )
}


export const allProvidersOverlap = (
  providerAvailability: ProviderAvailability[]
): boolean => {
  const latestOverlap = getLatestProviderOverlap(providerAvailability)
  const providers = providerAvailability.map( a => a.provider )
  return latestOverlap.sort().join('|') == providers.sort().join('|') 
}


export const getAvailableDateRange = ({
  providerAvailability,
  providers,
}: {
  providerAvailability: ProviderAvailability[] | undefined
  providers?: DataWarehouseProvider[]
}): [Date | null, Date | null] => {
  if( providers && providerAvailability ){
    providerAvailability = providerAvailability.filter( a => providers.includes(a.provider) )
  }
  if( !providerAvailability || !providerAvailability.length ){
    return [null, null]
  }
  const earliest = max(providerAvailability.map( a => a.start))
  const latest = min(providerAvailability.map( a => a.end))
  return [earliest, latest]
}


export const getValidDates = (...dates: (DateType | undefined | null)[]): Date[] => (
  dates.reduce( (acc, d) => {
    if( d ){
      acc.push(new Date(d))
    }
    return acc
  }, [] as Date[])
)

export type FilterPanelDateRange = {
  preset: DateRangePresetOption
  value: DateRangeValue
}


export const dateRangeValueToDateRangeUrlState = (
  dateRangeValue: FilterPanelDateRange
): DateRangeUrlState => ({
  dateRangePreset: dateRangeValue.preset,
  start: (
    dateRangeValue.preset === 'custom' ?
      isoDate(new Date(dateRangeValue.value[0])) :
      undefined
  ),
  end: (
    dateRangeValue.preset === 'custom' ?
      isoDate(new Date(dateRangeValue.value[1])) :
      undefined
  ),
})


export const resolveDateRangeValue = ({
  dateRangePreset,
  start,
  end,
  minDate,
  maxDate,
}: {
  dateRangePreset: DateRangePresetOption
  start: DateType | undefined | null
  end: DateType | undefined | null
  minDate: Date | null
  maxDate: Date | null
}): FilterPanelDateRange => {
  let dateRangeValue = [null, null] as unknown as DateRangeValue
  if( dateRangePreset === 'custom' ){
    const startDates = getValidDates(minDate, start)
    let resolvedStart = startDates.length ? max(startDates) : null
    const endDates = getValidDates(maxDate, end)
    let resolvedEnd = endDates.length ? min(endDates) : null
    if( maxDate && minDate && resolvedStart && resolvedEnd ){
      if( isAfter(resolvedStart, resolvedEnd) ){
        resolvedStart = minDate
      }
      if( isBefore(resolvedEnd, resolvedStart) ){
        resolvedEnd = maxDate
      }
    }
    dateRangeValue = [resolvedStart, resolvedEnd] as DateRangeValue
  }else{
    if( maxDate ){
      dateRangeValue = dateRangeCalculators[dateRangePreset](maxDate)
      if( minDate && dateRangeValue[0] ){
        dateRangeValue[0] = max([minDate, new Date(dateRangeValue[0])])
      }
    }
  }
  return {
    preset: dateRangePreset,
    value: dateRangeValue,
  }
}

export const isProviderCompatible = ({
  provider,
  selectedProviders,
  providerAvailability,
}: {
  provider: DataWarehouseProvider
  selectedProviders: DataWarehouseProvider[]
  providerAvailability: ProviderAvailability[] | undefined
}): boolean => {
  if( selectedProviders.includes(provider) ){
    return true
  }
  if( !providerAvailability || !providerAvailability.length ){
    return false
  }
  const targetAvailability = providerAvailability.find( a => a.provider === provider )
  if( !targetAvailability ){
    return false
  }
  if( !selectedProviders.length ){
    return true
  }
  const selectedAvailability = providerAvailability.filter(
    a => selectedProviders.includes(a.provider)
  )
  const minDate = max(selectedAvailability.map( a => a.start))
  const maxDate = min(selectedAvailability.map( a => a.end))
  return !(
    isBefore(targetAvailability.end, minDate)
    || isAfter(targetAvailability.start, maxDate)
  )
}


export const useOrgUnitHierarchy = ({
  orgUnitId
}: {
  orgUnitId: string | null
}): PlatformUnit | null => {
  const [allOrgUnits] = useUserPlatformUnits()
  return useMemo(() => {
    if( !allOrgUnits.data || !orgUnitId ){
      return null
    }

    let orgUnitHierarchy: PlatformUnit | null = null

    const walk = (o: PlatformUnit): void => {
      if( o.id === orgUnitId ){
        orgUnitHierarchy = o
        throw new Error('Found it')
      }
      if( isPlatformUnitContainer(o) ){
        o.children && o.children.forEach(walk)
      }
    }

    try{
      allOrgUnits.data.forEach(walk)
    }catch (e){
      noop()
    }

    return orgUnitHierarchy
  }, [orgUnitId, allOrgUnits.data])
}
